import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import AdminHeaderNew from '../../NewAdminPanel/Common/AdminHeader';
import AdminFooterNew from '../../NewAdminPanel/Common/AdminFooter';
import AdminSideBarNew from '../../NewAdminPanel/Common/AdminSidebarMnt';

const containerStyle = {
  width: "100%",
  height: "50vh",
};

const center = {
  lat: 28.641832439372656,
  lng: 77.22480777651072,
};

const CHCDetailView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    chcId,
    farmerData: initialFarmerData,
    surveyorUserCode: stateSurveyorUserCode,
    returnTo,
    expandedRows,
    expandedSurveyorRows,
    surveyorDetailsByDistrict,
    chcDetailsBySurveyor,
    farmerDetailsBySurveyor,
  } = location.state || {};
  const farmerCode = initialFarmerData?.farmerCode;

  // Debug location state
  React.useEffect(() => {
    console.log('🔍 CHCDetailView - Location state:', location.state);
    console.log('🔍 CHCDetailView - chcId:', chcId);
    console.log('🔍 CHCDetailView - farmerData:', initialFarmerData);
    console.log('🔍 CHCDetailView - farmerCode:', farmerCode);
    console.log('🔍 CHCDetailView - surveyorUserCode from state:', stateSurveyorUserCode);
  }, [location.state, chcId, initialFarmerData, farmerCode, stateSurveyorUserCode]);
  const [polygonData, setPolygonData] = useState(null);
  const [chcData, setChcData] = useState(null);
  const [imageData, setImageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState();
  const [map, setMap] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyD4X4kzlpReymgcXUXGh0Q3TalmxJmasM0",
  });

  // Debug Google Maps loading
  React.useEffect(() => {
    console.log('🎯 Google Maps Loading Status:', { isLoaded, loadError });
    if (loadError) {
      console.error('❌ Google Maps Load Error:', loadError);
    }
  }, [isLoaded, loadError]);

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo, {
        state: {
          expandedRows,
          expandedSurveyorRows,
          surveyorDetailsByDistrict,
          chcDetailsBySurveyor,
          farmerDetailsBySurveyor,
          restoreState: true,
          crmDemoVisible: location.state?.crmDemoVisible,
          chcHistoryVisible: location.state?.chcHistoryVisible,
          crmDemoDetails: location.state?.crmDemoDetails,
          chcDetails: location.state?.chcDetails,
          expensiveCrmVisible: location.state?.expensiveCrmVisible,
          expensiveCrmDetails: location.state?.expensiveCrmDetails,
        },
      });
      return;
    }
    navigate(-1);
  };

  const onLoad = React.useCallback(function callback(map) {
    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps) {
      console.error('Google Maps API not loaded');
      return;
    }

    if (chcData && chcData.polygonShape) {
      try {
        const coordinates = JSON.parse(chcData.polygonShape);
        
        // Validate coordinates
        if (!Array.isArray(coordinates) || coordinates.length === 0) {
          console.error('Invalid polygon coordinates');
          return;
        }

        const polygon = new window.google.maps.Polygon({
          paths: coordinates,
          strokeColor: "#FF0000",
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: "#FF0000",
          fillOpacity: 0.1,
        });

        polygon.setMap(map);

        // Create bounds to center the map
        const bounds = new window.google.maps.LatLngBounds();
        coordinates.forEach(coord => {
          if (coord.lat && coord.lng) {
            bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
          }
        });

        if (!bounds.isEmpty()) {
          map.fitBounds(bounds);

          // Add marker at center
          new window.google.maps.Marker({
            position: bounds.getCenter(),
            map: map,
            title: "CHC Location",
          });
        }
      } catch (error) {
        console.error('Error creating polygon:', error);
      }
    }
  }, [chcData]);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  const handleImageClick = (index) => {
    setActiveImage(index);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        console.log('🎯 CHCDetailView - Fetching data for chcId:', chcId);
        
        // Use surveyorUserCode from navigation state if available, otherwise use sessionStorage
        const surveyorUserCode = stateSurveyorUserCode || parseInt(sessionStorage.getItem('userCodeModel'));
        console.log('🎯 CHCDetailView - Using surveyorUserCode:', surveyorUserCode);
        
        // Fetch CHC details using the provided API
        const chcResponse = await axios.post(
          `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/Get_CHCDetailByID`,
          {
            surveyorUserCode: parseInt(surveyorUserCode)
          },
          {
            headers: {
              'accept': '*/*',
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('🎯 CHCDetailView - API Response:', chcResponse.data);

        if (chcResponse.data.success && chcResponse.data.dataContent?.length > 0) {
          // Find the specific CHC by ID
          const specificChc = chcResponse.data.dataContent.find(chc => chc.chcId === chcId);
          console.log('🎯 CHCDetailView - Found specific CHC:', specificChc);
          
          if (specificChc) {
            setChcData(specificChc);
            console.log('✅ CHCDetailView - CHC data set successfully');
          } else {
            console.log('❌ CHCDetailView - No CHC found with ID:', chcId);
            console.log('Available CHC IDs:', chcResponse.data.dataContent.map(chc => chc.chcId));
          }
        } else {
          console.log('❌ CHCDetailView - No data received from API');
        }

        // Fetch CHC image data
        const specificChc = chcResponse.data.dataContent.find(chc => chc.chcId === chcId);
        if (specificChc && farmerCode) {
          try {
            console.log('🔍 CHCDetailView - Fetching images for CHC ID:', chcId);
            console.log('🔍 CHCDetailView - Using farmerCode from state:', farmerCode);
            console.log('🔍 CHCDetailView - farmerCode type:', typeof farmerCode);
            
            const imageResponse = await axios.post(
              `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/Get_CHC_ImgByID`,
              {
                chcId: chcId.toString()
              },
              {
                headers: {
                  'accept': '*/*',
                  'Content-Type': 'application/json'
                }
              }
            );

            console.log('🎯 CHCDetailView - Image API Response:', imageResponse.data);

            if (imageResponse.data.success && imageResponse.data.dataContent?.length > 0) {
              setImageData(imageResponse.data.dataContent);
              console.log('✅ CHCDetailView - Images loaded successfully:', imageResponse.data.dataContent.length);
              console.log('🖼️ CHCDetailView - First image data:', imageResponse.data.dataContent[0]);
              console.log('🖼️ CHCDetailView - Image path:', imageResponse.data.dataContent[0]?.imagePath || imageResponse.data.dataContent[0]?.attPath);
            } else {
              console.log('⚠️ CHCDetailView - No images found in response');
            }
          } catch (imageError) {
            console.log('❌ CHCDetailView - Image fetch error:', imageError.response?.status, imageError.response?.data);
          }
        } else {
          console.log('⚠️ CHCDetailView - Cannot fetch images - missing farmerCode or CHC data');
          console.log('🔍 CHCDetailView - farmerCode available:', !!farmerCode);
          console.log('🔍 CHCDetailView - specificChc available:', !!specificChc);
        }
      } catch (error) {
        console.error('❌ CHCDetailView - Error fetching CHC details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (chcId) {
      fetchData();
    } else {
      console.log('❌ CHCDetailView - No chcId provided');
      setLoading(false);
    }
  }, [chcId]);

  // Debug image data
  React.useEffect(() => {
    console.log('🖼️ CHCDetailView - Image data state:', imageData);
    console.log('🖼️ CHCDetailView - Image data length:', imageData.length);
  }, [imageData]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <AdminHeaderNew />
      <AdminSideBarNew />
      <main id="main" className="main">
         <div className="pagetitle">
           <div className="d-flex justify-content-between align-items-center">
             <div>
               <h1>CHC Details</h1>
               <nav>
                 <ol className="breadcrumb">
                   <li className="breadcrumb-item">Home</li>
                   <li className="breadcrumb-item active">CHC Details</li>
                 </ol>
               </nav>
             </div>
             <button
               type="button"
               className="btn btn-success"
               onClick={handleBack}
             >
               <i>
                 <FontAwesomeIcon icon={faArrowLeft} />
               </i>{" "}
               Back
             </button>
           </div>
         </div>
         <section className="section">
            <div className="row">
              <div className="col-lg-12 dashboard">
                <div className="card">
                  <div className="card-body p-5 pt-2">
                    {chcData && (
                      <>
                        {/* Map Section */}
                        {chcData.polygonShape && (
                          <div className="row cropview">
                            <h5 className="formgrouping">CHC Location Map</h5>
                            {isLoaded ? (
                              <GoogleMap
                                mapContainerStyle={containerStyle}
                                center={center}
                                zoom={18}
                                onLoad={onLoad}
                                onUnmount={onUnmount}
                                mapTypeId="satellite"
                              ></GoogleMap>
                            ) : loadError ? (
                              <div className="alert alert-warning" style={{ height: '50vh' }}>
                                <h6>Map Loading Error</h6>
                                <p>Unable to load Google Maps. Please check your internet connection.</p>
                                <small>Error: {loadError.message}</small>
                              </div>
                            ) : (
                              <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Loading Map...</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Basic CHC Information */}
                        <div className="row cropview">
                          <h5 className="formgrouping">CHC Basic Information</h5>
                          <div className="row surveydetailsbg">
                            <div className="col-md-3">
                              <h6>CHC Name</h6>
                              <p>{chcData.chcName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Contact Person</h6>
                              <p>{chcData.contactPersonName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Mobile Number</h6>
                              <p>{chcData.mobileNo || 'N/A'}</p>
                            </div>
                            {/* <div className="col-md-3">
                              <h6>GST Number</h6>
                              <p>{chcData.gsT_NO || 'N/A'}</p>
                            </div> */}
                          </div>
                        </div>

                        {/* Location Information */}
                        <div className="row cropview">
                          <h5 className="formgrouping">Location Details</h5>
                          <div className="row surveydetailsbg">
                            <div className="col-md-3">
                              <h6>State</h6>
                              <p>{chcData.stateName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>District</h6>
                              <p>{chcData.districtName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Sub-District</h6>
                              <p>{chcData.subdistrictName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Village</h6>
                              <p>{chcData.villageName || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="row surveydetailsbg">
                            <div className="col-md-6">
                              <h6>Landmark Address</h6>
                              <p>{chcData.landMarkAddress || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                              <h6>Area Type</h6>
                              <p>{chcData.areaType === '1' ? 'Rural' : 'Urban'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Establishment Information */}
                        <div className="row cropview">
                          <h5 className="formgrouping">Establishment Details</h5>
                          <div className="row surveydetailsbg">
                            <div className="col-md-3">
                              <h6>Established By</h6>
                              <p>{chcData.establishedByName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Establishment Year</h6>
                              <p>{chcData.year || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Establishment Month</h6>
                              <p>{chcData.monthName || 'N/A'}</p>
                            </div>
                            {/* <div className="col-md-3">
                              <h6>Society Registration Number</h6>
                              <p>{chcData.societyRegistrationNumber || 'N/A'}</p>
                            </div> */}
                          </div>
                        </div>

                        {/* Scheme Information */}
                       {/* <div className="row cropview">
                          <h5 className="formgrouping">Scheme Details</h5>
                          <div className="row surveydetailsbg">
                            <div className="col-md-3">
                              <h6>Scheme Name</h6>
                              <p>{chcData.schemeName || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Number of Implements</h6>
                              <p>{chcData.noOfImplements || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Organization Type</h6>
                              <p>{chcData.orgTypeCode || 'N/A'}</p>
                            </div>
                            <div className="col-md-3">
                              <h6>Created On</h6>
                              <p>{chcData.createdOn ? new Date(chcData.createdOn).toLocaleDateString() : 'N/A'}</p>
                            </div>
                          </div>
                        </div>*/} 

                        {/* Polygon Information */}
                        {chcData.polygonShape && (
                          <div className="row cropview">
                            <h5 className="formgrouping">Area Details</h5>
                            <div className="row surveydetailsbg">
                              <div className="col-md-6">
                                <h6>Polygon Area (sq meters)</h6>
                                <p>{chcData.polygonArea || 'N/A'}</p>
                              </div>
                              <div className="col-md-6">
                                <h6>Area in Acres</h6>
                                <p>{chcData.polygonArea ? (chcData.polygonArea * 0.000247105).toFixed(4) + ' acres' : 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Image Details */}
                    {imageData.length > 0 && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Image Details</h5>
                        {imageData.map((image, index) => (
                          <Fragment key={index}>
                            <div className="col-md-2">
                              <img
                                src={image.imagePath || image.attPath}
                                alt={`CHC Image ${index + 1}`}
                                data-bs-toggle="modal"
                                data-bs-target="#staticBackdrop"
                                className="img-fluid imgdetials"
                                onClick={() => handleImageClick(index)}
                                style={{ cursor: 'pointer' }}
                                onError={(e) => {
                                  console.log('❌ Image failed to load:', e.target.src);
                                  e.target.style.display = 'none';
                                }}
                                onLoad={() => {
                                  console.log('✅ Image loaded successfully:', image.imagePath || image.attPath);
                                }}
                              />
                            </div>
                            <div className="col-md-4">
                              <div className="row imgdetialsbg">
                                <div className="col-md-6">
                                  <h6>Latitude</h6>
                                  <p>{image.surveyorLat || image.latitude || 'N/A'}</p>
                                </div>
                                <div className="col-md-6">
                                  <h6>Longitude</h6>
                                  <p>{image.surveyorLong || image.longitude || 'N/A'}</p>
                                </div>
                              </div>
                            </div>
                          </Fragment>
                        ))}
                      </div>
                    )}

                    {!chcData && (
                      <div className="alert alert-warning">
                        <h5>CHC Not Found</h5>
                        <p>No CHC data available for CHC ID: <strong>{chcId}</strong></p>
                        <p>This could be due to:</p>
                        <ul>
                          <li>Invalid CHC ID</li>
                          <li>CHC data not yet loaded</li>
                          <li>Permission issues</li>
                        </ul>
                        <button 
                          className="btn btn-primary" 
                          onClick={handleBack}
                        >
                          Go Back
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
      </main>
      <AdminFooterNew />

      {/* Image Modal */}
      {imageData.length > 0 && (
        <div
          className="modal fade"
          id="staticBackdrop"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="staticBackdropLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="staticBackdropLabel">
                  CHC Image
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  X
                </button>
              </div>
              <div className="modal-body">
                {activeImage !== undefined && (
                  <img
                    src={imageData[activeImage].imagePath || imageData[activeImage].attPath}
                    alt={`CHC Image ${activeImage + 1}`}
                    className="img-fluid w-100"
                    onError={(e) => {
                      console.log('❌ Modal image failed to load:', e.target.src);
                      e.target.src = '/path/to/placeholder.jpg'; // Add a placeholder if needed
                    }}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CHCDetailView;

